import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { dataTableParamsBuilder } from "../../utils/data-table-params-builder";
import { usePaginationQuery } from "../../utils/hooks/use-pagination-query";
import { http } from "../../utils/http";

function fetchInventory(params, sort) {
  const baseUrl = window.location.origin;
  const endpointUrl = new URL("/admin/product-inventories", baseUrl);

  if (params.categoryId) {
    endpointUrl.searchParams.append("categoryId", params.categoryId);
  }

  if (params.name) {
    endpointUrl.searchParams.append("name", params.name);
  }

  if (params.ref) {
    endpointUrl.searchParams.append("ref", params.ref);
  }

  if (params.from) {
    endpointUrl.searchParams.append("from", params.from);
  }
  if (params.to) {
    endpointUrl.searchParams.append("to", params.to);
  }

  if (sort.orderBy) {
    endpointUrl.searchParams.append("orderBy", sort.orderBy);
  }

  dataTableParamsBuilder({
    searchParams: endpointUrl.searchParams,
    filterValues: [params.order],
    ...params,
    ...sort,
  });

  return http(endpointUrl.toString().replace(baseUrl, ""));
}

export function useInventoryList(params = {}, sort = {}) {
  return usePaginationQuery(
    [
      "product-groups",
      params.ref,
      params.categoryId,
      params.name,
      params.from,
      params.to,
      sort.orderBy,
    ],
    (paginationState) => fetchInventory({ ...params, ...paginationState }, sort)
  );
}

async function addInventory(formData) {
  const res = await http(`/admin/product-groups`, {
    data: formData,
  });
  return res;
}

export function useAddInventory() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation(addInventory, {
    onSuccess: async (res) => {
      if (res?.status === 201 || res?.status === 200) {
        await queryClient.invalidateQueries(["product-groups"]);
        setTimeout(() => {
          navigate("/product-management/product-group");
        }, 1500);
      }
    },
  });
}

export function useUpdateInventories() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation(
    async (data) => {
      const { id, ...payload } = data;
      const res = await http(`/admin/product-groups/${id}`, {
        method: "PUT",
        data: payload,
      });
      return res;
    },
    {
      onSuccess: async (res) => {
        if (res !== undefined) {
          await queryClient.invalidateQueries(["product-groups"]);
          setTimeout(() => {
            navigate("/product-management/product-group/");
          }, 1500);
        }
      },
      onError: () => {
        console.error("Something when wrong");
      },
    }
  );
}

async function deleteInventories(id) {
  const data = await http(`/admin/product-groups/${id}`, {
    method: "DELETE",
  });

  return data;
}

export function useDeleteInventories() {
  const navigate = useNavigate();

  return useMutation(deleteInventories, {
    onSuccess: () => {
      setTimeout(() => {
        navigate("/product-management/product-group");
      }, 1500);
    },
  });
}

export async function fetchInventoriesDownload() {
  const { data } = await http(`/admin/product-inventories-download`, {
    responseType: "blob",
  });

  return data;
}

export function useInventoriesDownload() {
  const { data, ...raResult } = useQuery(["product-inventories-download"], () =>
    fetchInventoriesDownload()
  );

  return {
    ...raResult,
    data: data ?? {},
  };
}
